import React from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaMapMarkerAlt, FaInstagram, FaHeart, FaStar, FaLeaf, FaUsers, FaBook, FaGraduationCap, FaComments } from 'react-icons/fa';
import ContactForm from '../components/ContactForm';
import LiveChat from '../components/LiveChat';

declare global {
  interface Window {
    $crisp: any;
  }
}

const ContactUs: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-gray-800 py-12" style={{ direction: 'ltr', textAlign: 'left' }}>
      <div className="max-w-4xl mx-auto px-6" style={{ direction: 'ltr' }}>
        {/* Header */}
        <div className="text-center mb-12">
          <div className="relative">
            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full opacity-60"></div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full opacity-60"></div>
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full opacity-60"></div>
            
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 flex items-center justify-center relative z-10">
              <span className="ml-3 text-5xl">📬</span>
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Contact Us
              </span>
            </h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent font-medium">
            Hello and salaam 👋
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 mb-8 border border-indigo-100 dark:border-purple-900/30" style={{ direction: 'ltr' }}>
          <div className="prose prose-lg dark:prose-invert max-w-none" style={{ direction: 'ltr' }}>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              I'm <strong className="text-indigo-600 dark:text-indigo-400">Negin Nickparsa</strong> — a Persian-American, lifelong Farsi enthusiast, and founder of Pingquack Inc.
            </p>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              Whether you're helping your partner learn Farsi, supporting your children in staying connected to their Persian roots, or you're a second-generation Iranian hoping to reconnect with your culture — I created this platform for you.
            </p>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
              Learning Farsi isn't just about language — it's about stories, celebrations, and soul. It's about feeling connected to your roots and your relationships. I hope this website helps you, your loved ones, and your community speak Persian with pride.
            </p>

            {/* About Me Section */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 mb-8 border border-blue-200 dark:border-blue-800/30" style={{ direction: 'ltr' }}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center" style={{ direction: 'ltr' }}>
                <span className="ml-2 text-3xl">👩‍💼</span>
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">About Me</span>
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                I'm a business consultant and solution architect, combining technical strategy with creative vision. With years of experience in digital transformation, platform design, and user-focused solutions, I help both businesses and individuals build strong, scalable, and culturally meaningful systems.
              </p>
            </div>

            {/* About Pingquack Section */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-6 mb-8 border border-green-200 dark:border-green-800/30" style={{ direction: 'ltr' }}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center" style={{ direction: 'ltr' }}>
                <span className="ml-2 text-3xl">🐧</span>
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">About Pingquack Inc.</span>
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Pingquack Inc. is a cross-disciplinary creative and consulting company with three main divisions:
              </p>
              <ul className="space-y-6 text-gray-700 dark:text-gray-300" style={{ direction: 'ltr' }}>
                <li className="flex items-start" style={{ direction: 'ltr' }}>
                  <span className="text-orange-500 ml-3 font-bold text-lg bg-orange-100 dark:bg-orange-900/30 px-3 py-1 rounded-full">1.</span>
                  <div className="ml-3" style={{ direction: 'ltr' }}>
                    <strong className="text-lg text-green-700 dark:text-green-400">Children's Publishing</strong>
                    <p className="mt-2">We produce imaginative, illustrated books and Farsi learning tools that spark joy, curiosity, and cultural pride — especially for young readers.</p>
                  </div>
                </li>
                <li className="flex items-start" style={{ direction: 'ltr' }}>
                  <span className="text-purple-500 ml-3 font-bold text-lg bg-purple-100 dark:bg-purple-900/30 px-3 py-1 rounded-full">2.</span>
                  <div className="ml-3" style={{ direction: 'ltr' }}>
                    <strong className="text-lg text-green-700 dark:text-green-400">Digital Learning Platforms</strong>
                    <p className="mt-2">We design and develop educational technology solutions that make learning accessible, engaging, and culturally relevant.</p>
                  </div>
                </li>
                <li className="flex items-start" style={{ direction: 'ltr' }}>
                  <span className="text-blue-500 ml-3 font-bold text-lg bg-blue-100 dark:bg-blue-900/30 px-3 py-1 rounded-full">3.</span>
                  <div className="ml-3" style={{ direction: 'ltr' }}>
                    <strong className="text-lg text-green-700 dark:text-green-400">Business Consulting</strong>
                    <p className="mt-2">We provide strategic consulting services to help organizations build inclusive, culturally-aware systems and processes.</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Let's Connect Section */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 mb-8 border border-purple-200 dark:border-purple-800/30" style={{ direction: 'ltr' }}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center" style={{ direction: 'ltr' }}>
                <span className="ml-2 text-3xl">💌</span>
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Let's connect!</span>
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Have a question? Want to collaborate or just say salam? I'd love to hear from you.
              </p>
              
              <div className="space-y-4" style={{ direction: 'ltr' }}>
                <div className="flex items-center bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 border border-purple-200 dark:border-purple-800/30" style={{ direction: 'ltr' }}>
                  <FaComments className="text-purple-500 mr-3 text-xl" />
                  <div className="flex-1" style={{ direction: 'ltr' }}>
                    <strong className="text-gray-900 dark:text-white">Live Chat:</strong>
                    <p className="text-gray-600 dark:text-gray-400 text-sm ml-2">Get instant help and answers to your questions</p>
                  </div>
                  <button 
                    onClick={() => {
                      if (window.$crisp) {
                        window.$crisp.push(['do', 'chat:show']);
                        window.$crisp.push(['do', 'chat:open']);
                      }
                    }}
                    className="ml-4 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 font-medium text-sm shadow-lg"
                  >
                    Start Chat
                  </button>
                </div>
                
                <div className="flex items-center bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 border border-purple-200 dark:border-purple-800/30" style={{ direction: 'ltr' }}>
                  <FaEnvelope className="text-purple-500 mr-3 text-xl" />
                  <div style={{ direction: 'ltr' }}>
                    <strong className="text-gray-900 dark:text-white">Email:</strong>
                    <a 
                      href="mailto:support@pingquack.com" 
                      className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 ml-2 transition-colors font-medium"
                    >
                      support@pingquack.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 border border-purple-200 dark:border-purple-800/30" style={{ direction: 'ltr' }}>
                  <FaMapMarkerAlt className="text-purple-500 mr-3 text-xl" />
                  <div style={{ direction: 'ltr' }}>
                    <strong className="text-gray-900 dark:text-white">Based in:</strong>
                    <span className="text-gray-700 dark:text-gray-300 ml-2">Laguna Hills, California</span>
                  </div>
                </div>
                
                <div className="flex items-center bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 border border-purple-200 dark:border-purple-800/30" style={{ direction: 'ltr' }}>
                  <FaInstagram className="text-purple-500 mr-3 text-xl" />
                  <div style={{ direction: 'ltr' }}>
                    <strong className="text-gray-900 dark:text-white">Instagram:</strong>
                    <a 
                      href="https://instagram.com/pingquack" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 ml-2 transition-colors font-medium"
                    >
                      @pingquack
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Signature */}
            <div className="text-center border-t border-gray-200 dark:border-gray-700 pt-8" style={{ direction: 'ltr' }}>
              <p className="text-gray-600 dark:text-gray-400 mb-2" style={{ direction: 'ltr' }}>
                With warmth and wonder,
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white flex items-center justify-center" style={{ direction: 'ltr' }}>
                <FaHeart className="text-red-500 mr-2 animate-pulse" />
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Negin Nickparsa</span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" style={{ direction: 'ltr' }}>
                Founder, Pingquack Inc.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="mb-12">
          <ContactForm />
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl p-8 text-white shadow-2xl border border-indigo-400/30">
            <div className="flex items-center justify-center mb-4">
              <FaStar className="text-yellow-300 mr-2 text-2xl" />
              <h3 className="text-2xl font-bold text-white">Ready to start your Farsi learning journey?</h3>
              <FaStar className="text-yellow-300 ml-2 text-2xl" />
            </div>
            <p className="mb-6 opacity-90 text-lg">
              Join our community and discover the beauty of Persian culture and language.
            </p>
            <Link to="/learning-alphabet">
              <button className="px-8 py-4 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg">
                Get Started Today
              </button>
            </Link>
          </div>
        </div>

        {/* Decorative Footer */}
        <div className="mt-12 text-center">
          <div className="flex justify-center space-x-4 mb-4">
            <div className="w-4 h-4 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full animate-bounce"></div>
            <div className="w-4 h-4 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-4 h-4 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Made with ❤️ for the Persian community
          </p>
        </div>
      </div>

      {/* Live Chat Widget */}
      <LiveChat />
    </div>
  );
};

export default ContactUs; 